import React from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutPanelTop, MousePointerClick } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type {
  DemoPlacement, DemoPlacementOptions, PlacementSpot, VisualizerSurface,
} from '../../../common/hooks/use-demo-sites-api.hook';

/** The spot to start from for a surface: the suggested one, else the first. */
export const suggestedSpot = (options: DemoPlacementOptions | undefined, surface: VisualizerSurface): string | undefined => {
  const mine = (options?.spots || []).filter((s) => s.surface === surface);
  return (mine.find((s) => s.recommended) || mine[0])?.id;
};

const SPOT_LABEL: Record<PlacementSpot['kind'], string> = {
  replace: 'SUPER_ADMIN.DEMO_GENERATOR.SPOT_REPLACE',
  'add-to-cart': 'SUPER_ADMIN.DEMO_GENERATOR.SPOT_ADD_TO_CART',
  title: 'SUPER_ADMIN.DEMO_GENERATOR.SPOT_TITLE',
  gallery: 'SUPER_ADMIN.DEMO_GENERATOR.SPOT_GALLERY',
  floating: 'SUPER_ADMIN.DEMO_GENERATOR.SPOT_FLOATING',
};

interface Props {
  options?: DemoPlacementOptions;
  value: DemoPlacement;
  onChange: (next: DemoPlacement) => void;
  disabled?: boolean;
}

/**
 * Where the visualizer goes on the demo: a button or the widget, at one of the
 * spots the scan found, shown numbered on a picture of the page.
 */
export const VisualizerPlacementPicker: React.FC<Props> = ({ options, value, onChange, disabled }) => {
  const { t } = useTranslation();
  const spots = (options?.spots || []).filter((s) => s.surface === value.surface);
  const preview = options?.previews?.[value.surface];

  const changeSurface = (next: string) => {
    if (next !== 'button' && next !== 'widget') return;
    onChange({ surface: next, spot: suggestedSpot(options, next) });
  };

  return (
    <section className="flex flex-col gap-3 border-t pt-4" data-testid="visualizer-placement">
      <div>
        <h3 className="text-sm font-medium">{t('SUPER_ADMIN.DEMO_GENERATOR.PLACEMENT_TITLE')}</h3>
        <p className="text-sm text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.PLACEMENT_HINT')}</p>
      </div>

      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        value={value.surface}
        onValueChange={changeSurface}
        disabled={disabled}
        className="self-start"
      >
        <ToggleGroupItem value="button">
          <MousePointerClick />
          {t('SUPER_ADMIN.DEMO_GENERATOR.PLACEMENT_BUTTON')}
        </ToggleGroupItem>
        <ToggleGroupItem value="widget">
          <LayoutPanelTop />
          {t('SUPER_ADMIN.DEMO_GENERATOR.PLACEMENT_WIDGET')}
        </ToggleGroupItem>
      </ToggleGroup>
      <p className="text-xs text-muted-foreground">
        {t(value.surface === 'widget' ? 'SUPER_ADMIN.DEMO_GENERATOR.PLACEMENT_WIDGET_HINT' : 'SUPER_ADMIN.DEMO_GENERATOR.PLACEMENT_BUTTON_HINT')}
      </p>

      {spots.length ? (
        <>
          <div role="radiogroup" aria-label={t('SUPER_ADMIN.DEMO_GENERATOR.PLACEMENT_TITLE')} className="grid gap-2 sm:grid-cols-2">
            {spots.map((spot, i) => {
              const selected = spot.id === value.spot;
              return (
                <button
                  key={spot.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={disabled}
                  onClick={() => onChange({ surface: value.surface, spot: spot.id })}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border bg-background p-3 text-start transition-colors hover:bg-muted/50 disabled:opacity-60',
                    selected && 'outline outline-[1.5px] outline-foreground',
                  )}
                >
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-amber-500 text-xs font-medium text-black">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      {t(SPOT_LABEL[spot.kind])}
                      {spot.recommended && <Badge variant="secondary">{t('SUPER_ADMIN.DEMO_GENERATOR.SPOT_SUGGESTED')}</Badge>}
                    </span>
                    {spot.text && <span className="block truncate text-xs text-muted-foreground">“{spot.text}”</span>}
                  </span>
                </button>
              );
            })}
          </div>
          {preview ? (
            <a href={preview} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-lg border" title={t('SUPER_ADMIN.DEMO_GENERATOR.PLACEMENT_PREVIEW_OPEN')}>
              <img src={preview} alt={t('SUPER_ADMIN.DEMO_GENERATOR.PLACEMENT_PREVIEW_ALT')} className="block w-full" />
            </a>
          ) : (
            <p className="text-sm text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.PLACEMENT_NO_PREVIEW')}</p>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.PLACEMENT_NONE')}</p>
      )}
    </section>
  );
};
